import React, { type ReactNode } from 'react';

interface AdminModalShellProps {
    title: string;
    onClose: () => void;
    children: ReactNode;
    /** Panel width: default md; product forms use 2xl */
    size?: 'md' | 'lg' | '2xl' | '3xl';
}

const sizeClass: Record<NonNullable<AdminModalShellProps['size']>, string> = {
    md: 'max-w-md',
    lg: 'max-w-lg',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
};

/**
 * Centered admin modal: full-viewport overlay, comfortable width, scrollable body.
 * Sticky header with Cancel so long forms are easy to exit without scrolling to the bottom.
 */
const AdminModalShell: React.FC<AdminModalShellProps> = ({
    title,
    onClose,
    children,
    size = 'md',
}) => {
    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-modal-title"
                className={`flex max-h-[90vh] w-full ${sizeClass[size]} flex-col overflow-hidden rounded-lg bg-white shadow-2xl`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sticky top bar — always visible while body scrolls */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-6 py-4">
                    <h2 id="admin-modal-title" className="min-w-0 truncate text-xl font-bold text-gray-800">
                        {title}
                    </h2>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-xl leading-none text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
            </div>
        </div>
    );
};

export default AdminModalShell;
